import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { FileExtensionPipe } from '@modules/attachments/pipes/file-extension.pipe';
import { DropzoneContentComponent } from '@modules/shared/dropzone-content/dropzone-content.component';
import { FileExtension } from '@shared/models/enums/file-extension.enum';
import { TuiNotificationService } from '@taiga-ui/core';
import { TuiFiles } from '@taiga-ui/kit';

@Component({
  selector: 'pxc-attachment-drag-and-drop',
  imports: [DropzoneContentComponent, FormsModule, TranslocoDirective, TuiFiles],
  providers: [FileExtensionPipe],
  templateUrl: './attachment-drag-and-drop.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachmentDragAndDropComponent {
  @Input() allowedExtensions: string[] = Object.values(FileExtension);

  @Output() files = new EventEmitter<File[]>();

  fileAcceptTypes = this.allowedExtensions.join(', ');

  private readonly alertService = inject(TuiNotificationService);
  private readonly fileExtensionPipe = inject(FileExtensionPipe);
  private readonly translocoService = inject(TranslocoService);

  onFilesAdded(files: File | readonly File[] | null): void {
    this.files.emit(this.handleFiles(files ? [files].flat() : []));
  }

  private handleFiles(userFiles: File[]): File[] {
    const files = userFiles.filter((file: File) => this.isValidExtension(file.name));

    if (userFiles.length !== files.length) {
      this.showUnsupportedFilesWarning();
    }

    return files;
  }

  private isValidExtension(filename: string): boolean {
    const extension = this.fileExtensionPipe.transform(filename);
    return extension ? this.allowedExtensions.includes(extension) : false;
  }

  showUnsupportedFilesWarning(): void {
    this.alertService
      .open(this.translocoService.translate("L'extension de certains fichiers n'est pas pris en charge"), {
        label: this.translocoService.translate('Fichier(s) non supporté(s)'),
        appearance: 'warning',
      })
      .subscribe();
  }
}
